import { FetchResult } from '@apollo/client'
import { CircularProgress, Grid } from '@material-ui/core'
import * as R from 'ramda'
import React, { useMemo } from 'react'
import { FormProvider, SubmitHandler, useForm, UseFormMethods } from 'react-hook-form'

import {
  DeleteCustomerDispensaryPromoDocument,
  PutCustomerDispensaryPromoDocument,
} from '../../../../../graphql/camel'
import useCamelMutation from '../../../../../hooks/useCamelMutation'
import useLogAndCaptureError from '../../../../../hooks/useLogAndCaptureError'
import { RequiredSelectField, RequiredTextField } from '../../../../form/RequiredFields'
import TextField from '../../../../form/TextField'
import {
  StyledAlert,
  StyledButtonLink,
  StyledFieldContainer,
  StyledPrimaryButton,
  StyledSecondaryButton,
} from './styled'
import { CustomerDispensaryPromo, MutatePromoProps } from './types'

export interface DispensaryEditPromoDropdownProps extends MutatePromoProps {
  promo: CustomerDispensaryPromo
}

interface EditPromoFormValues {
  dispensaryCode: string
  expirationDate: string
  rootPromoId: string
}

const resetFormIfSuccessful = (reset: UseFormMethods['reset']) =>
  R.tap(function <TData>({ errors }: FetchResult<TData>) {
    if (errors && errors.length) {
      return
    }

    reset()
  })

const DispensaryEditPromoDropdown: React.FC<DispensaryEditPromoDropdownProps> = ({
  dispensarySlug,
  existingCodes,
  existingOneTimeOnlyIds,
  promo,
  rootPromos,
  refetchQueries,
}) => {
  const methods = useForm<EditPromoFormValues>()
  const { formState, handleSubmit, setError, errors, reset } = methods
  const [putDispensaryPromo, { loading: putLoading, error: putError }] = useCamelMutation(
    PutCustomerDispensaryPromoDocument,
    { refetchQueries },
  )
  const [deleteDispensaryPromo, { loading: deleteLoading, error: deleteError }] = useCamelMutation(
    DeleteCustomerDispensaryPromoDocument,
    { refetchQueries },
  )

  useLogAndCaptureError(putError)
  useLogAndCaptureError(deleteError)

  const rootPromoOptions = useMemo(
    () =>
      rootPromos.reduce((acc: { label: string; value: string }[], rootPromo) => {
        if (!rootPromo) return acc
        if (promo.rootPromoId !== rootPromo.id && existingOneTimeOnlyIds.includes(rootPromo.id)) {
          return acc
        }
        acc.push({
          label: rootPromo.name,
          value: rootPromo.id,
        })
        return acc
      }, []),
    [existingOneTimeOnlyIds, promo.rootPromoId, rootPromos],
  )

  const onSubmit: SubmitHandler<EditPromoFormValues> = async (data) => {
    if (
      data.dispensaryCode !== promo.dispensaryCode &&
      existingCodes.includes(data.dispensaryCode.toUpperCase())
    ) {
      setError('dispensaryCode', {
        message: 'Code already exists',
        type: 'validate',
      })
      return
    }
    await putDispensaryPromo({
      variables: {
        dispensarySlug,
        promoId: promo.id,
        customerDispensaryPromoUpdateInput: {
          dispensaryCode: data.dispensaryCode,
          expirationDate: data.expirationDate,
          inactive: false,
          rootPromoId: data.rootPromoId,
        },
      },
    }).then(resetFormIfSuccessful(reset))
  }

  const deactivatePromo = async () => {
    await putDispensaryPromo({
      variables: {
        dispensarySlug,
        promoId: promo.id,
        customerDispensaryPromoUpdateInput: {
          dispensaryCode: promo.dispensaryCode,
          expirationDate: promo.expirationDate,
          inactive: true,
          rootPromoId: promo.rootPromoId,
        },
      },
    })
  }

  const reactivatePromo = async () => {
    if (existingCodes.includes(promo.dispensaryCode)) {
      setError('dispensaryCode', {
        message: 'An active promo already has this code',
        type: 'validate',
      })
      return
    }
    if (!rootPromoOptions.map((rootPromo) => rootPromo.value).includes(promo.rootPromoId)) {
      if (existingOneTimeOnlyIds.includes(promo.rootPromoId)) {
        setError('rootPromoId', {
          message: `Only one active code is allowed per "${promo.rootPromoName}"`,
          type: 'validate',
        })
        return
      }
      setError('rootPromoId', { message: 'Promotion is no longer available', type: 'validate' })
      return
    }
    await putDispensaryPromo({
      variables: {
        dispensarySlug,
        promoId: promo.id,
        customerDispensaryPromoUpdateInput: {
          dispensaryCode: promo.dispensaryCode,
          expirationDate: promo.expirationDate,
          inactive: false,
          rootPromoId: promo.rootPromoId,
        },
      },
    }).then(resetFormIfSuccessful(reset))
  }

  const deletePromo = async () => {
    await deleteDispensaryPromo({
      variables: { dispensarySlug, promoId: promo.id },
    })
  }

  const isInactive = promo.inactive

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container item xs={12} spacing={5} alignItems="center" justify="space-between">
          <Grid item xs={12}>
            <StyledFieldContainer>
              <RequiredTextField
                name="dispensaryCode"
                label="Code"
                defaultValue={promo.dispensaryCode}
                disabled={isInactive}
              />
            </StyledFieldContainer>
          </Grid>
          <Grid item xs={12}>
            <StyledFieldContainer>
              <RequiredSelectField
                name="rootPromoId"
                label="Promotion"
                options={rootPromoOptions}
                defaultValue={promo.rootPromoId}
                disabled={isInactive}
              />
            </StyledFieldContainer>
          </Grid>
          <Grid item xs={12}>
            <StyledFieldContainer>
              <TextField
                name="expirationDate"
                label="Expiration Date"
                type="date"
                defaultValue={promo.expirationDate}
                disabled={isInactive}
                InputLabelProps={{ shrink: true }}
                InputProps={{ inputProps: { min: new Date().toISOString().substring(0, 10) } }}
              />
            </StyledFieldContainer>
          </Grid>
          {!!putError && (
            <Grid item xs={12}>
              <StyledAlert type="error">
                An error occurred updating promotion. Please try again soon.
              </StyledAlert>
            </Grid>
          )}
          {!!deleteError && (
            <Grid item xs={12}>
              <StyledAlert type="error">
                An error occurred deleting promotion. Please try again soon.
              </StyledAlert>
            </Grid>
          )}
          {!!errors.dispensaryCode?.message && (
            <Grid item xs={12}>
              <StyledAlert type="error">{errors.dispensaryCode.message}</StyledAlert>
            </Grid>
          )}
          <Grid item xs={4} sm={3}>
            <StyledButtonLink
              onClick={isInactive ? reactivatePromo : deactivatePromo}
              disabled={formState.isSubmitting || putLoading}
            >
              {formState.isSubmitting || putLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                `${isInactive ? 'Reactivate' : 'Deactivate'}`
              )}
            </StyledButtonLink>
          </Grid>
          {!isInactive ? (
            <Grid item xs={4} sm={3}>
              <StyledPrimaryButton type="submit" disabled={formState.isSubmitting || putLoading}>
                {formState.isSubmitting || putLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Update'
                )}
              </StyledPrimaryButton>
            </Grid>
          ) : (
            <Grid item xs={4} sm={3}>
              <StyledSecondaryButton
                onClick={deletePromo}
                disabled={formState.isSubmitting || deleteLoading}
              >
                {formState.isSubmitting || deleteLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Delete'
                )}
              </StyledSecondaryButton>
            </Grid>
          )}
        </Grid>
      </form>
    </FormProvider>
  )
}

export default DispensaryEditPromoDropdown
